import { useEffect, useState } from "preact/hooks"
import { Button } from "@/components/ui/button"
import { api_fetch } from "src/api/client.tsx"
import { toast } from "@/components/ui/use-toast"
import { loadOrgSettings, useOrgSettings } from "@/org_settings/state.tsx"
import { classes } from "@/utils"

interface DemoResetReport {
  elapsed_ms: number
}

/** Shown on every Settings page of a sales-demo org: puts the demo back to its starting state. */
export function DemoResetBanner({ className }: { className?: string }) {
  const { is_demo } = useOrgSettings()
  const [resetting, setResetting] = useState(false)

  // org settings are cached across orgs; refresh them so the banner follows the selected org
  useEffect(() => {
    void loadOrgSettings()
  }, [])

  if (!is_demo) return null

  async function reset_demo() {
    if (!window.confirm("Reset the demo? Everything done in this org since the last reset is erased.")) return
    setResetting(true)
    const result = await api_fetch<DemoResetReport>("/organization/demo-reset", { method: "POST", body: {} })
    if (result.ok) {
      const seconds = (result.value.data.elapsed_ms / 1000).toFixed(1)
      toast({ title: "Demo reset", description: `Fresh demo data, dated today (${seconds}s).` })
      // a full reload drops every cached page, so the demo starts from clean data
      window.setTimeout(() => window.location.assign("/"), 800)
    } else {
      setResetting(false)
      toast({ title: "Demo reset failed", description: result.error.message, variant: "destructive" })
    }
  }

  return (
    <div
      class={classes(
        "flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3",
        className
      )}>
      <div class="text-sm">
        <p class="font-medium text-amber-900">This is a demo organization</p>
        <p class="text-amber-800">Reset it before each demo to erase disputes, tasks and wizard runs, and move every date to today.</p>
      </div>
      <Button onClick={reset_demo} disabled={resetting}>
        {resetting ? "Resetting…" : "Reset demo data"}
      </Button>
    </div>
  )
}
